import React, { useState, useContext } from 'react'
import { useRouter } from 'next/router'
import { ErrorBelowInputField } from './elements/Errors'
import { InputText } from './elements/Inputs'
import SubmitButton, { ButtonFontSize } from './elements/SubmitButton'
import { VerifyZipDataFormContext } from '../context/VerifyZipDataFormContext'

const isValidZip = (zip: string) => /^\d{5,}$/.test(zip)

const VerifyZipCodeForm: React.FunctionComponent = () => {
  const router = useRouter()
  const [originZip, setOriginZip] = useState('')
  const [destZip, setDestZip] = useState('')
  const [loading, setLoading] = useState(false)
  const [formError, setFormError] = useState(false)
  const { verifyZipDataFormApiResponse, setVerifyZipDataFormApiResponse } =
    useContext(VerifyZipDataFormContext)

  const submitForm = async () => {
    const originValid = isValidZip(originZip)
    const destValid = isValidZip(destZip)

    if (!originValid || !destValid) {
      setVerifyZipDataFormApiResponse({
        errozp: !originValid,
        errdzp: !destValid,
        ...(!originValid && {
          emsgozp: 'Please enter a valid 5 digit zip code.',
        }),
        ...(!destValid && {
          emsgdzp: 'Please enter a valid 5 digit zip code.',
        }),
      })
      return
    }

    try {
      setLoading(true)
      const response = await fetch('/api/internal-api/validate-zipcodes', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          originZipCode: Number(originZip),
          destinationZipCode: Number(destZip),
        }),
      })

      if (!response.ok) {
        throw new Error(
          `Failed to fetch at api/internal-api/validate-zipcodes: ${response.status}.`
        )
      }

      const data = await response.json()
      const originServicePoint = data?.originServicePoint
      const destinationServicePoint = data?.destinationServicePoint

      if (!originServicePoint || !destinationServicePoint) {
        setLoading(false)
        setVerifyZipDataFormApiResponse({
          errozp: !originServicePoint,
          errdzp: !destinationServicePoint,
          ...(!originServicePoint && {
            emsgozp: 'Origin ZIP code is invalid or not serviceable.',
          }),
          ...(!destinationServicePoint && {
            emsgdzp: 'Destination ZIP code is invalid or not serviceable.',
          }),
        })
        return
      }

      const originIsDirect = originServicePoint.serviceType === 'D'
      const destinationIsDirect = destinationServicePoint.serviceType === 'D'

      if (!originIsDirect && !destinationIsDirect) {
        setLoading(false)
        const interlineMessage =
          'Origin and destination ZIP codes are both outside our direct service area. Please contact sales at sales@nopk.biz.'
        setVerifyZipDataFormApiResponse({
          errozp: true,
          errdzp: true,
          emsgozp: interlineMessage,
          emsgdzp: interlineMessage,
        })
        return
      }

      router.push({
        pathname: '/rate-estimate',
        query: {
          originZip: originZip,
          destZip: destZip,
        },
      })
    } catch (error: any) {
      setLoading(false)
      setFormError(true)
      console.error('api/internal-api/validate-zipcodes failed')
      console.log(error)
    }
  }

  return (
    <section>
      <form
        onSubmit={(event: any) => {
          event.preventDefault()
          submitForm()
        }}
      >
        <div className="mt-3 mb-4 md:mt-6 md:mb-6 md:text-nptBase lg:mt-7 lg:mb-[1.865rem]">
          <InputText
            labelText="Destination"
            type="text"
            placeholder="Zip Code"
            value={destZip}
            characterMax={5}
            onChange={(event: any) => {
              setDestZip(event.target.value)
              setVerifyZipDataFormApiResponse({})
            }}
          />

          {verifyZipDataFormApiResponse.errdzp && (
            <ErrorBelowInputField
              errorMessage={verifyZipDataFormApiResponse.emsgdzp}
            />
          )}
        </div>

        <div className="mb-4 md:mb-8 lg:">
          <InputText
            labelText="Origin"
            type="text"
            placeholder="Zip Code"
            value={originZip}
            characterMax={5}
            onChange={(event: any) => {
              setOriginZip(event.target.value)
              setVerifyZipDataFormApiResponse({})
            }}
          />

          {verifyZipDataFormApiResponse.errozp && (
            <ErrorBelowInputField
              errorMessage={verifyZipDataFormApiResponse.emsgozp}
            />
          )}
        </div>

        <SubmitButton
          onClick={() => {
            return false
          }}
          text={loading ? 'Validating Zipcodes...' : 'Get Rate Estimate'}
          isDisabled={originZip && destZip ? (loading ? true : false) : true}
          fontSize={ButtonFontSize.small}
        />
        {formError && (
          <ErrorBelowInputField errorMessage="Please try again later." />
        )}
      </form>
    </section>
  )
}

export default VerifyZipCodeForm
